import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Suspense } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { getQuestionnaireBySlug } from "@/lib/questionnaires.functions";
import { QuestionnaireForm } from "@/components/cockpit/questionnaires/QuestionnaireForm";
import { ConsentGate, ConsentBadge } from "@/components/cockpit/questionnaires/ConsentGate";

export const Route = createFileRoute("/_cockpit/cockpit/fragebogen/$slug")({
  head: () => ({
    meta: [
      { title: "Fragebogen – Cockpit – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: FragebogenDetailPage,
  notFoundComponent: () => (
    <div className="mx-auto w-full min-w-0 max-w-3xl">
      <h1 className="font-display text-3xl font-extrabold text-primary">
        Fragebogen nicht verfügbar
      </h1>
      <p className="mt-3 text-muted-foreground">
        Dieser Fragebogen ist für Sie nicht freigegeben.
      </p>
      <Link
        to="/cockpit/fragebogen"
        className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-extrabold text-primary hover:brightness-95"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Zurück zur Übersicht
      </Link>
    </div>
  ),
});

const TYPE_LABEL: Record<string, string> = {
  personaldatenblatt: "Personaldatenblatt",
  wahlkampf: "Wahlkampf",
  ttpa: "TTPA",
  design: "Design",
  design_standard: "Standard-Kampagnendesign",
  test: "Test",
};
const VARIANT_LABEL: Record<string, string> = {
  bundestagswahl: "Bundestagswahl",
  landtagswahl: "Landtagswahl",
  landratswahl: "Landratswahl",
  buergermeisterwahl: "Bürgermeisterwahl",
  oberbuergermeisterwahl: "Oberbürgermeisterwahl",
  kommunalwahl_direktmandat: "Kommunalwahl (Direktmandat)",
  europawahl: "Europawahl",
};

function FragebogenDetailPage() {
  const { slug } = Route.useParams();
  return (
    <div className="mx-auto w-full min-w-0 max-w-6xl">
      <Link
        to="/cockpit/fragebogen"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Zurück zur Übersicht
      </Link>
      <Suspense
        fallback={
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-32 animate-pulse rounded-lg bg-secondary" />
            ))}
          </div>
        }
      >
        <ConsentGate>
          <Loaded slug={slug} />
        </ConsentGate>
      </Suspense>
    </div>
  );
}

function Loaded({ slug }: { slug: string }) {
  const { data } = useSuspenseQuery({
    queryKey: ["cockpit", "questionnaires", "detail", slug],
    queryFn: () => getQuestionnaireBySlug({ data: { slug } }),
    retry: false,
  });

  if (!data) throw notFound();

  const t = data.template;
  const readOnly = data.response.status === "completed";
  const subtitle =
    t.type === "wahlkampf" && t.variant
      ? `${TYPE_LABEL[t.type] ?? t.type} · ${VARIANT_LABEL[t.variant] ?? t.variant}`
      : TYPE_LABEL[t.type] ?? t.type;

  return (
    <div>
      <header className="mb-6">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {subtitle}
        </span>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-primary md:text-4xl">
          {t.title}
        </h1>
        {t.description && (
          <p className="mt-3 text-muted-foreground">{t.description}</p>
        )}
        <ConsentBadge />
      </header>
      <QuestionnaireForm detail={data} readOnly={readOnly} />
    </div>
  );
}
